import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Card,
  CardContent,
  Alert,
  Skeleton,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as FileCopyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  FilterList as FilterIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import PageContainer from "../components/common/PageContainer";
import TemplateItem from "../components/templates/TemplateItem";
import SearchInput from "../components/common/SearchInput";
import ConfirmationDialog from "../components/common/ConfirmationDialog";
import LoadingState from "../components/common/LoadingState";
import { useNavigate } from "react-router-dom";
import {
  searchTemplates,
  filterTemplatesByCategory,
  getTemplateCategories,
  duplicateTemplate,
  getTemplateDisplayName,
} from "../models/Template";
import { generateMockTemplates } from "../utils/mockData";
import { templateService } from "../services";

const Templates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Load templates on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        // Simulate API call delay
        // await new Promise((resolve) => setTimeout(resolve, 800));
        const dataRetrieved = await templateService.getTemplates();
        console.log(dataRetrieved);

        // Generate mock templates using the new data model
        // const mockTemplates = generateMockTemplates(12);
        setTemplates(dataRetrieved);
      } catch (error) {
        console.error("Error loading templates:", error);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  // Get available categories from templates
  const availableCategories = getTemplateCategories(templates);

  // Filter templates based on tab and search term
  const getFilteredTemplates = () => {
    let filtered = [...templates];

    // Filter by tab
    if (tabValue === 1) {
      // Favorites - for demo, we'll mark some as favorites
      filtered = filtered.filter((template, index) => index % 3 === 0);
    } else if (tabValue === 2) {
      filtered = filterTemplatesByCategory(filtered, "safety");
    } else if (tabValue === 3) {
      filtered = filterTemplatesByCategory(filtered, "maintenance");
    } else if (tabValue === 4) {
      filtered = filterTemplatesByCategory(filtered, "inspection");
    } else if (tabValue === 5) {
      filtered = filtered.filter(
        (template) =>
          template.metadata.category === "compliance" ||
          template.metadata.category === "quality"
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = searchTemplates(filtered, searchTerm);
    }

    return filtered;
  };

  const filteredTemplates = getFilteredTemplates();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleTemplateClick = (template) => {
    console.log(template);
    // navigate(`/templates/${templateId}`, { state: template });
    navigate(`/templates/${template.id}`, { state: template });
  };

  const handleMenuOpen = (event, template) => {
    setAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTemplate(null);
  };

  const handleEdit = () => {
    navigate(`/templates/${selectedTemplate.id}`);
    handleMenuClose();
  };

  const handleDelete = () => {
    setConfirmDialogOpen(true);
    handleMenuClose();
  };

  const handleDuplicate = () => {
    if (selectedTemplate) {
      const duplicatedTemplate = duplicateTemplate(selectedTemplate, {
        name: `${selectedTemplate.name} (Copy)`,
        createdBy: "current_user_id", // This would come from auth context
      });

      // Add to templates list
      setTemplates((prev) => [duplicatedTemplate, ...prev]);

      // Navigate to edit the new template
      navigate(`/templates/${duplicatedTemplate.id}`);
    }
    handleMenuClose();
  };

  const handleShare = () => {
    // Implement sharing functionality
    console.log("Share template:", selectedTemplate);
    handleMenuClose();
  };

  const confirmDelete = () => {
    if (selectedTemplate) {
      // Remove template from list
      setTemplates((prev) => prev.filter((t) => t.id !== selectedTemplate.id));
      setConfirmDialogOpen(false);
      setSelectedTemplate(null);
    }
  };

  const handleToggleFavorite = (templateId) => {
    // For demo purposes, we'll just log this
    // In a real app, this would update the template's favorite status
    console.log("Toggle favorite for template:", templateId);
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      general: "default",
      safety: "error",
      maintenance: "success",
      inspection: "primary",
      quality: "info",
      compliance: "secondary",
      training: "warning",
      audit: "warning",
    };
    return colorMap[category] || "default";
  };

  if (loading) {
    return (
      <PageContainer title="Templates">
        <LoadingState />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Template Management"
      subtitle={`${templates.length} templates available`}
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/templates/new")}
        >
          New Template
        </Button>
      }
    >
      {/* Search and Filter */}
      {/* <Box sx={{ mb: 3 }}>
        <SearchInput
          placeholder="Search templates by name, description, or tags..."
          onSearch={handleSearch}
          showFilter={true}
          onFilterClick={() => console.log("Open advanced filter dialog")}
        />
      </Box> */}

      {/* Category Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="template category tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={`All (${templates.length})`} />
          {/* <Tab label="Favorites" /> */}
          {/* <Tab label="Safety" />
          <Tab label="Maintenance" />
          <Tab label="Inspection" />
          <Tab label="Compliance & Quality" /> */}
        </Tabs>
      </Box>

      {/* Template Statistics */}
      {/* {tabValue === 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" color="primary">
                  {templates.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Templates
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" color="success.main">
                  {templates.filter((t) => t.isActive).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Templates
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" color="info.main">
                  {availableCategories.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Categories
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )} */}

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          {searchTerm ? (
            <Alert severity="info" sx={{ maxWidth: 400, mx: "auto" }}>
              No templates found matching "{searchTerm}". Try adjusting your
              search terms.
            </Alert>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                No templates found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Get started by creating your first template
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/templates/new")}
              >
                Create Template
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredTemplates.map((template) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
              <TemplateItem
                template={template}
                onClick={handleTemplateClick}
                onMenuClick={handleMenuOpen}
                onToggleFavorite={handleToggleFavorite}
                showStats={true}
                compact={false}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Template</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <FileCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleShare}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Template"
        message={
          selectedTemplate
            ? `Are you sure you want to delete "${getTemplateDisplayName(
                selectedTemplate
              )}"? This action cannot be undone and will affect any checklists using this template.`
            : "Are you sure you want to delete this template?"
        }
        confirmText="Delete"
        confirmColor="error"
      />
    </PageContainer>
  );
};

export default Templates;
